import { Injectable } from '@angular/core';
import { CaseService } from '@secca/core/services/case.service';
import { DocumentService } from '@secca/core/services/document.service';
import { ExchangeRateService } from '@secca/core/services/exchange-rate.service';
import { IncidentService } from '@secca/core/services/incident.service';
import { PlannedTravelService } from '@secca/core/services/planned-travel.service';
import { RefundService } from '@secca/core/services/refund.service';
import { SupplierInvoiceService } from '@secca/core/services/supplier-invoice.service';
import { SupplierService } from '@secca/core/services/supplier.service';
import { CaseStateService } from '@secca/core/state-services/case-state.service';
import { AtlasFraudDetectionDialogData } from '@secca/shared/components/atlas-chatbot/fraud-detection-dialog/atlas-fraud-detection-dialog.component';
import {
  getDiagnosisSources,
  formatIncidentLocation
} from '@secca/shared/helpers/atlas/atlas-case-context-format.helper';
import { formatDisplayDate, serializeDateForAtlas } from '@secca/shared/helpers/atlas/atlas-date-format-helper';
import { toNonEmptyString } from '@secca/shared/helpers/string-helper';
import { CaseDocument } from '@secca/shared/models/caseDocument';
import { CaseDocumentTagEnum, RefundStatus, ServiceTypeEnum, SupplierInvoiceStatus } from '@secca/shared/models/enums';
import {
  FraudDetectionDocument,
  FraudDetectionRequest,
  FraudDetectionResponse,
  FraudIcd10Code,
  FraudServiceOrder
} from '@secca/shared/models/atlas/atlas-fraud-detection.model';
import { Case } from '@secca/shared/models/case';
import { ServiceOrder } from '@secca/shared/models/service-order/service-order';
import { firstValueFrom } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AtlasFraudDetectionContextService {
  private readonly fraudDetectionCurrency = 'EUR';
  private fraudDetectionExchangeRateCache = new Map<string, Promise<number>>();

  constructor(
    private caseService: CaseService,
    private caseStateService: CaseStateService,
    private incidentService: IncidentService,
    private plannedTravelService: PlannedTravelService,
    private supplierService: SupplierService,
    private supplierInvoiceService: SupplierInvoiceService,
    private refundService: RefundService,
    private documentService: DocumentService,
    private exchangeRateService: ExchangeRateService
  ) {}

  async buildDialogData(caseId: string | null, currentCaseNumber: string): Promise<AtlasFraudDetectionDialogData> {
    this.fraudDetectionExchangeRateCache.clear();
    const incident = caseId ? await this.getCurrentIncident(caseId) : null;
    const plannedTravel = caseId ? await this.getCurrentPlannedTravel(caseId) : null;
    const icd10Codes = this.getFraudDetectionIcd10Codes(incident);
    const primaryIcd10 = icd10Codes[0] || null;
    const countryCode = this.getFraudDetectionCountryCode(incident);
    const country = this.getFraudDetectionCountryDisplayValue(incident);
    const departureDate = serializeDateForAtlas(plannedTravel?.departureDate);
    const incidentDate = serializeDateForAtlas(incident?.incidentDate);
    const arrivalHomeDate = serializeDateForAtlas(plannedTravel?.arrivalDate);
    const serviceOrders = caseId ? await this.getCurrentFraudDetectionServiceOrders(caseId) : [];
    // Temporarily disabled: do not include parsed documents in fraud detection API requests.
    // const documents = caseId ? await this.getCurrentFraudDetectionDocuments(caseId) : [];
    const documents = [];

    return {
      caseNumber: currentCaseNumber,
      request: {
        case_number: currentCaseNumber === '-' ? undefined : currentCaseNumber,
        icd10: icd10Codes.length ? icd10Codes : undefined,
        country: countryCode || undefined,
        service_orders: serviceOrders,
        departure_date: departureDate || undefined,
        incident_date: incidentDate || undefined,
        arrival_home_date: arrivalHomeDate || undefined,
        documents: documents.length ? documents : undefined
      },
      fields: {
        caseNumber: currentCaseNumber === '-' ? null : currentCaseNumber,
        icd10: primaryIcd10?.code || null,
        icd10Source: primaryIcd10?.source || null,
        icd10List: icd10Codes,
        country,
        departureDate: formatDisplayDate(plannedTravel?.departureDate),
        incidentDate: formatDisplayDate(incident?.incidentDate),
        arrivalHomeDate: formatDisplayDate(plannedTravel?.arrivalDate)
      }
    };
  }

  hasFraudDetectionRequestInput(request: Partial<FraudDetectionRequest> | null | undefined): boolean {
    if (!request) {
      return false;
    }

    const hasIcd10 = Array.isArray(request.icd10) ? request.icd10.length > 0 : !!request.icd10;
    const hasServiceOrders = Array.isArray(request.service_orders) && request.service_orders.length > 0;
    return !!request.case_number || hasIcd10 || !!request.country || hasServiceOrders || !!request.departure_date || !!request.incident_date || !!request.arrival_home_date;
  }

  getFraudDetectionFlagCount(result: FraudDetectionResponse | null): number {
    const numberOfFlags = Number(result?.number_of_flags);
    if (Number.isFinite(numberOfFlags) && numberOfFlags > 0) {
      return numberOfFlags;
    }

    return (result?.score_details || []).filter(scoreDetail => !!scoreDetail.flagged).length;
  }

  normalizeFraudScoreTone(color: string | null | undefined): 'green' | 'yellow' | 'orange' | 'red' | null {
    const normalizedColor = String(color || '').toLowerCase();
    return normalizedColor === 'green' || normalizedColor === 'yellow' || normalizedColor === 'orange' || normalizedColor === 'red'
      ? normalizedColor
      : null;
  }

  private async getCurrentPlannedTravel(caseId: string): Promise<any | null> {
    try {
      return await firstValueFrom(this.plannedTravelService.getPlannedTravel(caseId));
    } catch {
      return null;
    }
  }

  private async getCurrentIncident(caseId: string): Promise<any | null> {
    try {
      const activeCase = await this.getCurrentCase(caseId);
      if (!activeCase?.incident?.id) {
        return null;
      }

      return await firstValueFrom(this.incidentService.getCaseIncident(activeCase.incident.id));
    } catch {
      return null;
    }
  }

  private async getCurrentCase(caseId: string): Promise<Case | null> {
    try {
      return await firstValueFrom(
        this.caseStateService.getCase().pipe(
          filter((activeCase): activeCase is Case => !!activeCase && String(activeCase.id) === String(caseId)),
          take(1),
          timeout(1000)
        )
      );
    } catch {
      return null;
    }
  }

  private async getCurrentFraudDetectionServiceOrders(caseId: string): Promise<FraudServiceOrder[]> {
    try {
      const numericCaseId = Number(caseId);
      const [serviceOrders, supplierInvoices, refunds, splitItems, supplierExpensesAppendixes, caseStakeholders] = await Promise.all([
        firstValueFrom(this.caseService.getServiceOrdersForCase(caseId)),
        firstValueFrom(this.supplierInvoiceService.getSupplierInvoicesByCase(numericCaseId)),
        firstValueFrom(this.refundService.getRefundByCaseId(numericCaseId)),
        firstValueFrom(this.supplierInvoiceService.getCoordinationCaseSplitItemsByCase(numericCaseId)),
        firstValueFrom(this.supplierInvoiceService.getExpenseAppendixByCase(numericCaseId)),
        firstValueFrom(this.caseService.getStakeholdersOnCase(caseId))
      ]);
      const treatmentOrders = serviceOrders.filter(serviceOrder => serviceOrder?.type === ServiceTypeEnum.TREATMENT);
      return await Promise.all(treatmentOrders.map(serviceOrder =>
        this.mapFraudServiceOrder(serviceOrder, supplierInvoices, refunds, splitItems, supplierExpensesAppendixes, caseStakeholders)
      ));
    } catch {
      return [];
    }
  }

  private async getCurrentFraudDetectionDocuments(caseId: string): Promise<FraudDetectionDocument[]> {
    try {
      const documents = await firstValueFrom(this.documentService.getCaseDocumentList(Number(caseId)));
      const relevantDocuments = (documents || []).filter(document => this.hasFraudDetectionDocumentTag(document));
      const mappedDocuments = await Promise.all(relevantDocuments.map(document => this.mapFraudDetectionDocument(document)));
      return mappedDocuments.filter(Boolean) as FraudDetectionDocument[];
    } catch {
      return [];
    }
  }

  private hasFraudDetectionDocumentTag(document: CaseDocument): boolean {
    return this.getFraudDetectionDocumentTags(document).some(tag => this.isFraudDetectionDocumentTag(tag));
  }

  private isFraudDetectionDocumentTag(tag: string): boolean {
    const normalizedTag = tag.trim().toUpperCase().replace(/[\s-]+/g, '_');
    return normalizedTag === 'MEDICAL_INFO' || normalizedTag === 'RECEIPT' || normalizedTag === 'CUSTOMER_INVOICE';
  }

  private getFraudDetectionDocumentTags(document: CaseDocument): string[] {
    return (document?.caseDocumentTags || [])
      .map(tag => toNonEmptyString(tag?.name))
      .filter(Boolean) as string[];
  }

  private async mapFraudDetectionDocument(document: CaseDocument): Promise<FraudDetectionDocument | null> {
    const documentId = toNonEmptyString(document?.documentId);
    if (!documentId) {
      return null;
    }

    try {
      const blob = await firstValueFrom(this.documentService.getFile(documentId, 'application', document?.documentExtension || 'octet-stream'));
      const base64Encoding = await this.blobToBase64(blob);
      return {
        file_name: this.getFraudDetectionDocumentFileName(document),
        base64_encoding: base64Encoding,
        tags: this.getFraudDetectionDocumentTags(document).map(tag => this.getFraudDetectionDocumentTagLabel(tag))
      };
    } catch {
      return null;
    }
  }

  private getFraudDetectionDocumentFileName(document: CaseDocument): string {
    const name = toNonEmptyString(document?.documentName) || toNonEmptyString(document?.documentId) || 'document';
    const extension = toNonEmptyString(document?.documentExtension);
    return extension && !name.toLowerCase().endsWith(`.${extension.toLowerCase()}`) ? `${name}.${extension}` : name;
  }

  private getFraudDetectionDocumentTagLabel(tag: string): string {
    const normalizedTag = tag.trim().toUpperCase().replace(/[\s-]+/g, '_');
    return CaseDocumentTagEnum[normalizedTag as keyof typeof CaseDocumentTagEnum] || tag;
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = typeof reader.result === 'string' ? reader.result : '';
        const base64 = result.includes(',') ? result.split(',').pop() : result;

        if (!base64) {
          reject(new Error('Failed to convert document to Base64.'));
          return;
        }

        resolve(base64);
      };
      reader.onerror = () => reject(reader.error ?? new Error('Failed to read document.'));
      reader.readAsDataURL(blob);
    });
  }

  private async mapFraudServiceOrder(
    serviceOrder: ServiceOrder,
    supplierInvoices: any[] = [],
    refunds: any[] = [],
    splitItems: any[] = [],
    supplierExpensesAppendixes: any[] = [],
    caseStakeholders: any[] = []
  ): Promise<FraudServiceOrder> {
    const supplierMetadata = await this.getFraudDetectionSupplierMetadata(serviceOrder?.supplierId, caseStakeholders);

    return {
      id: String(serviceOrder?.serviceOrderId ?? serviceOrder?.id ?? ''),
      service_type: String(serviceOrder?.type || ServiceTypeEnum.TREATMENT),
      supplier: {
        id: supplierMetadata.supplierId,
        name: supplierMetadata.supplierName,
        inpatient_recommendation: supplierMetadata.inpatientRecommendation,
        outpatient_recommendation: supplierMetadata.outpatientRecommendation
      },
      commitment_type: this.normalizeFraudCommitmentType(serviceOrder),
      reserve: await this.getFraudDetectionReserveInEuro(serviceOrder),
      original_cost: await this.getFraudDetectionOriginalCost(serviceOrder, supplierInvoices, refunds, splitItems, supplierExpensesAppendixes)
    };
  }

  private async getFraudDetectionOriginalCost(
    serviceOrder: ServiceOrder,
    supplierInvoices: any[] = [],
    refunds: any[] = [],
    splitItems: any[] = [],
    supplierExpensesAppendixes: any[] = []
  ): Promise<number> {
    const serviceOrderInvoices = this.getFraudDetectionSupplierInvoicesByServiceOrder(serviceOrder, supplierInvoices);
    let supplierInvoiceOriginalCost = 0;
    for (const supplierInvoice of serviceOrderInvoices) {
      const serviceOrderShare = (supplierInvoice?.items || [])
        .filter(item => item?.serviceOrderId === serviceOrder?.serviceOrderId)
        .reduce((itemSum, item) => itemSum + this.parseFraudAmount(item?.paidAmount), 0);
      const exchangedShare = serviceOrderShare * this.parseFraudAmount(supplierInvoice?.exchangeRate);
      const currency = this.getFraudDetectionAmountCurrency(supplierInvoice?.invoiceAmountExchanged?.currency);
      supplierInvoiceOriginalCost += await this.convertFraudDetectionAmountToEuro(exchangedShare, currency);
    }

    let supplierExpenseOriginalCost = 0;
    for (const expenses of (supplierExpensesAppendixes || []).filter(expenses => expenses?.serviceOrderId === serviceOrder?.serviceOrderId)) {
      supplierExpenseOriginalCost += await this.convertFraudDetectionAmountToEuro(
        this.parseFraudAmount(expenses?.amountInvoice?.amount),
        this.getFraudDetectionAmountCurrency(expenses?.amountInvoice?.currency)
      );
    }

    let splitInvoiceOriginalCost = 0;
    for (const item of (splitItems || []).filter(item => item?.serviceOrderId === serviceOrder?.serviceOrderId)) {
      splitInvoiceOriginalCost += await this.convertFraudDetectionAmountToEuro(
        this.parseFraudAmount(item?.splitMoneyCaseCurrency?.amount),
        this.getFraudDetectionAmountCurrency(item?.splitMoneyCaseCurrency?.currency)
      );
    }

    const refundOriginalCost = await this.getFraudDetectionRefundOriginalCost(serviceOrder, refunds);

    return supplierInvoiceOriginalCost + supplierExpenseOriginalCost + splitInvoiceOriginalCost + refundOriginalCost;
  }

  private getFraudDetectionSupplierInvoicesByServiceOrder(serviceOrder: ServiceOrder, supplierInvoices: any[]): any[] {
    return (supplierInvoices || []).filter(invoice => {
      if (invoice?.invoiceStatus === SupplierInvoiceStatus.invoiceReceived) {
        return serviceOrder?.serviceOrderId === invoice?.serviceOrderId;
      }

      return invoice?.invoiceStatus !== SupplierInvoiceStatus.rejected &&
        (invoice?.items || []).some(item => item?.serviceOrderId === serviceOrder?.serviceOrderId);
    });
  }

  private async getFraudDetectionRefundOriginalCost(serviceOrder: ServiceOrder, refunds: any[]): Promise<number> {
    let refundOriginalCost = 0;
    for (const refund of (refunds || []).filter(refund => this.isApprovedFraudDetectionRefund(refund))) {
      const exchangeRate = typeof refund?.getExchangeRate === 'function'
        ? this.parseFraudAmount(refund.getExchangeRate())
        : this.parseFraudAmount(refund?.exchangeRate);

      const refundServiceOrderShare = (refund?.items || [])
        .filter(item => item?.serviceOrderId === serviceOrder?.serviceOrderId)
        .reduce((itemSum, item) => itemSum + this.parseFraudAmount(item?.refundExchangedAmount) * exchangeRate, 0);
      const currency = this.getFraudDetectionAmountCurrency(
        refund?.refundCaseAmountExchanged?.currency ?? refund?.caseCurrency
      );
      refundOriginalCost += await this.convertFraudDetectionAmountToEuro(refundServiceOrderShare, currency);
    }

    return refundOriginalCost;
  }

  private isApprovedFraudDetectionRefund(refund: { refundStatus?: unknown } | null | undefined): boolean {
    const status = String(refund?.refundStatus || '');
    return status === String(RefundStatus.APPROVED) || status === String(RefundStatus.PAID);
  }

  private async getFraudDetectionSupplierMetadata(
    supplierId: number | string | null | undefined,
    caseStakeholders: any[] = []
  ): Promise<{
    supplierId: string;
    supplierName: string;
    inpatientRecommendation: string | null;
    outpatientRecommendation: string | null;
  }> {
    const caseStakeholderId = supplierId == null ? '' : String(supplierId).trim();
    if (!caseStakeholderId) {
      return {
        supplierId: '',
        supplierName: '',
        inpatientRecommendation: 'Unknown',
        outpatientRecommendation: 'Unknown'
      };
    }

    const supplierStakeholder = this.getFraudDetectionSupplierStakeholder(caseStakeholderId, caseStakeholders);
    const providerId = toNonEmptyString(supplierStakeholder?.company?.providerId);
    const stakeholderSupplierName = toNonEmptyString(supplierStakeholder?.company?.name);
    if (!providerId) {
      return {
        supplierId: '',
        supplierName: stakeholderSupplierName || '',
        inpatientRecommendation: 'Unknown',
        outpatientRecommendation: 'Unknown'
      };
    }

    try {
      const response = await firstValueFrom(
        this.supplierService.searchSuppliers(`freeText=supplierId:${providerId}`)
      );

      const suppliers = Array.isArray(response?.docList) ? response.docList : [];
      const supplier = suppliers.find(item => toNonEmptyString(item?.supplierName)) || suppliers[0];
      const supplierRecommendations = this.getFraudDetectionSupplierServiceRecommendationsFromDocuments(suppliers);
      return {
        supplierId: providerId,
        supplierName: stakeholderSupplierName || toNonEmptyString(supplier?.supplierName) || '',
        inpatientRecommendation: supplierRecommendations.inpatientRecommendation,
        outpatientRecommendation: supplierRecommendations.outpatientRecommendation
      };
    } catch {
      return {
        supplierId: providerId,
        supplierName: stakeholderSupplierName || '',
        inpatientRecommendation: null,
        outpatientRecommendation: null
      };
    }
  }

  private getFraudDetectionSupplierServiceRecommendationsFromDocuments(
    suppliers: Array<{ supplierService_list?: Array<{ service_aoo_name?: string; service_aoo_recommendation?: string }> }> = []
  ): { inpatientRecommendation: string | null; outpatientRecommendation: string | null } {
    return {
      inpatientRecommendation: this.getFraudDetectionSupplierServiceRecommendationFromDocuments(suppliers, 'Inpatient'),
      outpatientRecommendation: this.getFraudDetectionSupplierServiceRecommendationFromDocuments(suppliers, 'Outpatient')
    };
  }

  private getFraudDetectionSupplierServiceRecommendationFromDocuments(
    suppliers: Array<{ supplierService_list?: Array<{ service_aoo_name?: string; service_aoo_recommendation?: string }> }> = [],
    serviceAooName: 'Inpatient' | 'Outpatient'
  ): string | null {
    for (const supplier of suppliers) {
      const recommendation = this.getFraudDetectionSupplierServiceRecommendation(supplier?.supplierService_list, serviceAooName);
      if (recommendation) {
        return recommendation;
      }
    }

    return null;
  }

  private getFraudDetectionSupplierServiceRecommendation(
    supplierServices: Array<{ service_aoo_name?: string; service_aoo_recommendation?: string }> | null | undefined,
    serviceAooName: 'Inpatient' | 'Outpatient'
  ): string | null {
    const service = (supplierServices || []).find(value =>
      String(value?.service_aoo_name || '').trim().toLowerCase() === serviceAooName.toLowerCase()
    );

    return toNonEmptyString(service?.service_aoo_recommendation);
  }

  private getFraudDetectionSupplierStakeholder(caseStakeholderId: string, caseStakeholders: any[] = []): any | null {
    return (caseStakeholders || []).find(stakeholder =>
      String(stakeholder?.id ?? '').trim() === caseStakeholderId
    ) || null;
  }

  private normalizeFraudCommitmentType(serviceOrder: ServiceOrder | null | undefined): string {
    const extension = serviceOrder?.extension as { getCommitmentType?: () => any; commitmentType?: any } | null | undefined;
    const commitmentType = extension?.getCommitmentType?.() || extension?.commitmentType;
    const commitmentTypeName = String(commitmentType?.name || '').trim().toLowerCase();
    if (commitmentTypeName.includes('guarantee of payment') || /\bgop\b/i.test(commitmentTypeName)) {
      return 'Guarantee of Payment';
    }

    if (commitmentTypeName.includes('pay and claim')) {
      return 'Pay and Claim';
    }

    return 'Other';
  }

  private parseFraudAmount(value: string | number | null | undefined): number {
    const numericValue = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  }

  private async getFraudDetectionReserveInEuro(serviceOrder: ServiceOrder | null | undefined): Promise<number> {
    const exchangedCurrency = this.getFraudDetectionAmountCurrency(serviceOrder?.exchangedCostCurrency);
    if (exchangedCurrency === this.fraudDetectionCurrency && serviceOrder?.exchangedCostValue != null) {
      return this.parseFraudAmount(serviceOrder.exchangedCostValue);
    }

    return this.convertFraudDetectionAmountToEuro(
      this.parseFraudAmount(serviceOrder?.expectedCostValue),
      this.getFraudDetectionAmountCurrency(serviceOrder?.expectedCostCurrency)
    );
  }

  private async convertFraudDetectionAmountToEuro(amount: number, currency: string | null): Promise<number> {
    if (!amount || currency === this.fraudDetectionCurrency) {
      return amount;
    }

    if (!currency) {
      return amount;
    }

    const exchangeRate = await this.getFraudDetectionExchangeRate(currency, this.fraudDetectionCurrency);
    return Number((amount * exchangeRate).toFixed(2));
  }

  private getFraudDetectionAmountCurrency(value: unknown): string | null {
    const currency = toNonEmptyString(value);
    return currency ? currency.toUpperCase() : null;
  }

  private getFraudDetectionExchangeRate(sourceCurrency: string, targetCurrency: string): Promise<number> {
    const cacheKey = `${sourceCurrency}:${targetCurrency}`;
    if (!this.fraudDetectionExchangeRateCache.has(cacheKey)) {
      this.fraudDetectionExchangeRateCache.set(
        cacheKey,
        firstValueFrom(this.exchangeRateService.getExchangeRateNow(sourceCurrency, targetCurrency))
          .then(response => this.parseFraudAmount(response?.normalRate))
          .then(rate => rate || 0)
      );
    }

    return this.fraudDetectionExchangeRateCache.get(cacheKey) as Promise<number>;
  }

  private getFraudDetectionIcd10Codes(incident: any): FraudIcd10Code[] {
    const seenCodes = new Set<string>();
    return getDiagnosisSources(incident)
      .map(item => {
        const code = toNonEmptyString(item.diagnosis?.icd10Code);
        return code ? { code, source: item.source } : null;
      })
      .filter(Boolean)
      .filter(item => {
        const normalizedCode = item.code.toLowerCase();
        if (seenCodes.has(normalizedCode)) {
          return false;
        }

        seenCodes.add(normalizedCode);
        return true;
      }) as FraudIcd10Code[];
  }

  private getFraudDetectionCountryCode(incident: any): string | null {
    const incidentLocation = incident?.incidentLocation;
    const currentLocation = incident?.currentLocation;
    return this.normalizeAlpha2CountryCode(currentLocation?.countryCodeAlpha2) ||
      this.normalizeAlpha2CountryCode(incidentLocation?.countryCodeAlpha2) ||
      this.normalizeAlpha2CountryCode(incident?.countryCodeAlpha2) ||
      this.normalizeAlpha2CountryCode(currentLocation?.country) ||
      this.normalizeAlpha2CountryCode(incidentLocation?.country) ||
      this.normalizeAlpha2CountryCode(incident?.incidentCountry);
  }

  private getFraudDetectionCountryDisplayValue(incident: any): string | null {
    const incidentLocation = incident?.incidentLocation;
    const currentLocation = incident?.currentLocation;
    return toNonEmptyString(currentLocation?.country) ||
      toNonEmptyString(incidentLocation?.country) ||
      toNonEmptyString(incident?.incidentCountry) ||
      this.getFraudDetectionCountryCode(incident) ||
      formatIncidentLocation(incidentLocation);
  }

  private normalizeAlpha2CountryCode(value: any): string | null {
    const countryCode = toNonEmptyString(value);
    return countryCode && /^[a-z]{2}$/i.test(countryCode) ? countryCode.toUpperCase() : null;
  }
}
